import axios from 'axios';
import { toast } from 'react-toastify';

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Set the JWT token for authenticated requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

// Initialize token from localStorage if it exists
const token = localStorage.getItem('token');
if (token) {
  setAuthToken(token);
}

// Add a request interceptor to ensure token is included in every request
api.interceptors.request.use(
  config => {
    // Ensure token is added to each request
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common error scenarios
    const { response } = error;

    if (response) {
      // Handle specific status codes
      switch (response.status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.log('Authentication error:', error);
          
          // Only redirect if not a background API check
          const isBackgroundRequest = error.config.url.includes('/check-auth') || 
                                      error.config.headers['X-Background-Request'] === 'true';
          
          if (!isBackgroundRequest) {
            toast.error('Authentication session expired. Please log in again.');
            // Clear token and redirect to login
            setAuthToken(null);
            window.location.href = '/login';
          } else {
            console.log('Background authentication request failed, not redirecting');
          }
          break;
        case 403:
          // Forbidden - insufficient permissions
          toast.error('You do not have permission to perform this action.');
          break;
        case 404:
          // Not found
          console.error(`API 404 Error: ${error.config.url} not found`);
          toast.error(`Resource not found: ${error.config.url.split('/').pop()}`, {
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT
          });
          break;
        case 422:
          // Validation errors
          if (response.data?.errors) {
            const errors = response.data.errors;
            errors.forEach(err => toast.error(err.msg));
          } else {
            toast.error('Validation error. Please check your input.');
          }
          break;
        case 500:
          // Server error
          toast.error('Server error. Please try again later or contact support.');
          break;
        default:
          // Other errors
          toast.error(response.data?.error || 'An error occurred. Please try again.');
      }
    } else {
      // Network error or server not responding
      toast.error('Unable to connect to server. Please check your internet connection.');
    }

    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/authentication/login', credentials),
  register: (userData) => api.post('/authentication/register', userData),
  forgotPassword: (email) => api.post('/authentication/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/authentication/reset-password', { token, password }),
};

// User Management API
export const userAPI = {
  // Get all users with pagination and filtering
  getUsers: (params) => api.get('/comprehensive-crud/users', { params }),
  
  // Get a single user by ID with roles
  getUser: (id) => api.get(`/comprehensive-crud/users/${id}`),
  
  // Create a new user with role assignments
  createUser: async (userData) => {
    const formattedData = {
      ...userData,
      roles: userData.roles || [],
      customer_id: userData.customer_id || null,
      mobile_number: userData.mobile_number || userData.phone,
      is_active: userData.is_active !== undefined ? userData.is_active : true,
      isActive: userData.isActive !== false // Default to true if not specified
    };
    
    return api.post('/comprehensive-crud/users', formattedData);
  },
  
  // Update an existing user
  updateUser: (id, userData) => {
    const formattedData = { ...userData };
    if (formattedData.password === '') {
      delete formattedData.password;
    }
    return api.put(`/comprehensive-crud/users/${id}`, formattedData);
  },
  
  // Toggle user active status
  toggleUserStatus: (id, isActive) => 
    api.patch(`/comprehensive-crud/users/${id}`, { isActive }),
  
  // Delete a user
  deleteUser: (id) => api.delete(`/comprehensive-crud/users/${id}`),
  
  // Bulk upload users
  uploadBulkUsers: (formData) => api.post('/comprehensive-crud/users/bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  // Download user import template
  downloadUserTemplate: () => api.get('/comprehensive-crud/users/template', {
    responseType: 'blob'
  }),
};

// Role Management API
export const roleAPI = {
  getRoles: () => api.get('/comprehensive-crud/roles'),
  getRole: (id) => api.get(`/comprehensive-crud/roles/${id}`),
  createRole: (roleData) => api.post('/comprehensive-crud/roles', roleData),
  updateRole: (id, roleData) => api.put(`/comprehensive-crud/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/comprehensive-crud/roles/${id}`),
  uploadBulkRoles: (formData, onUploadProgress) => {
    return api.post('/role_management/roles/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    });
  },
  downloadRoleTemplate: () => {
    return api.get('/role_management/roles/template', {
      responseType: 'blob'
    }).then(response => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'role-template.csv');
      
      // Append to html page
      document.body.appendChild(link);
      
      // Force download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
    });
  }
};

// Permission Management API
export const permissionAPI = {
  getPermissions: () => api.get('/permission_management/permissions'),
  getPermission: (id) => api.get(`/permission_management/permissions/${id}`),
  createPermission: (permissionData) => api.post('/permission_management/permissions', permissionData),
  updatePermission: (id, permissionData) => api.put(`/permission_management/permissions/${id}`, permissionData),
  assignPermissions: (roleId, permissions) => api.post('/permission_management/assign', { role_id: roleId, permissions }),
};

// Logging API
export const loggingAPI = {
  getLogs: (params) => api.get('/logging/activity', { params }),
  getActionTypes: () => api.get('/logging/actions'),
  getEntityTypes: () => api.get('/logging/entities'),
  getStats: () => api.get('/logging/stats'),
};

// Client Management API
export const clientAPI = {
  getClients: async (params = {}) => {
    try {
      const response = await api.get('/clients', { params });
      return { data: response.data || [], success: true };
    } catch (error) {
      console.error('Error fetching clients:', error);
      return { data: [], error: error.response?.data?.error || 'Failed to fetch clients', success: false };
    }
  },
  getClient: async (id) => {
    try {
      const response = await api.get(`/clients/${id}`);
      return { data: response.data, success: true };
    } catch (error) {
      console.error(`Error fetching client ${id}:`, error);
      return { data: null, error: error.response?.data?.error || 'Failed to fetch client', success: false };
    }
  },
  createClient: async (clientData) => {
    try {
      const response = await api.post('/clients', clientData);
      return { data: response.data, success: true };
    } catch (error) {
      console.error('Error creating client:', error);
      return { 
        data: null, 
        error: error.response?.data?.error || 'Failed to create client',
        validationErrors: error.response?.data?.errors,
        success: false 
      };
    }
  },
  updateClient: async (id, clientData) => {
    try {
      const response = await api.put(`/clients/${id}`, clientData);
      return { data: response.data, success: true };
    } catch (error) {
      console.error(`Error updating client ${id}:`, error);
      return { 
        data: null, 
        error: error.response?.data?.error || 'Failed to update client',
        validationErrors: error.response?.data?.errors,
        success: false 
      };
    }
  },
  deleteClient: async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      return { success: true };
    } catch (error) {
      console.error(`Error deleting client ${id}:`, error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete client' 
      };
    }
  },
  uploadBulkClients: async (formData) => {
    try {
      const response = await api.post('/clients/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return { data: response.data, success: true };
    } catch (error) {
      console.error('Error uploading bulk clients:', error);
      return { 
        data: null, 
        error: error.response?.data?.error || 'Failed to upload clients',
        validationErrors: error.response?.data?.errors,
        success: false 
      };
    }
  },
  downloadClientTemplate: async () => {
    try {
      const response = await api.get('/clients/template', {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client-template.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading client template:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to download template' 
      };
    }
  }  
};

// Customer Management API
export const customerAPI = {
  getCustomers: (params) => api.get('/customers', { params }),
  getCustomer: (id) => api.get(`/customers/${id}`),
  createCustomer: (customerData) => api.post('/customers', customerData),
  updateCustomer: (id, customerData) => api.put(`/customers/${id}`, customerData),
  deleteCustomer: (id) => api.delete(`/customers/${id}`),
  uploadBulkCustomers: (formData) => api.post('/customers/bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  downloadCustomerTemplate: () => api.get('/customers/template', {
    responseType: 'blob'
  }).then(response => {
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'customer-template.csv');
    
    // Append to html page
    document.body.appendChild(link);
    
    // Force download
    link.click();
    
    // Clean up and remove the link
    link.parentNode.removeChild(link);
  })
};

export const eventAPI = {
  getEvents: async (params = {}) => {
    try {
      const response = await api.get('/events', { params });
      return { data: response.data || [], success: true };
    } catch (error) {
      console.error('Error fetching events:', error);
      return { data: [], error: error.response?.data?.error || 'Failed to fetch events', success: false };
    }
  },
  getEvent: async (id) => {
    try {
      const response = await api.get(`/events/${id}`);
      return { data: response.data, success: true };
    } catch (error) {
      console.error(`Error fetching event ${id}:`, error);
      return { data: null, error: error.response?.data?.error || 'Failed to fetch event', success: false };
    }
  },
  createEvent: async (eventData) => {
    try {
      const response = await api.post('/events', eventData);
      return { data: response.data, success: true };
    } catch (error) {
      console.error('Error creating event:', error);
      return {
        data: null,
        error: error.response?.data?.error || 'Failed to create event',
        validationErrors: error.response?.data?.errors,
        success: false
      };
    }
  },
  updateEvent: async (id, eventData) => {
    try {
      const response = await api.put(`/events/${id}`, eventData);
      return { data: response.data, success: true };
    } catch (error) {
      console.error(`Error updating event ${id}:`, error);
      return {
        data: null,
        error: error.response?.data?.error || 'Failed to update event',
        validationErrors: error.response?.data?.errors,
        success: false
      };
    }
  },
  deleteEvent: async (id) => {
    try {
      await api.delete(`/events/${id}`);
      return { success: true };
    } catch (error) {
      console.error(`Error deleting event ${id}:`, error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete event'
      };
    }
  },
  getEventsByClient: async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/events`);
      return { data: response.data || [], success: true };
    } catch (error) {
      console.error(`Error fetching events for client ${clientId}:`, error);
      return { data: [], error: 'Failed to fetch client events', success: false };
    }
  },
  getEventSchedule: async (eventId) => {
    try {
      // Use the correct endpoint for subevents
      const response = await api.get(`/comprehensive-crud/event-schedule/${eventId}`);
      return { 
        success: true, 
        data: Array.isArray(response.data) ? response.data : (response.data?.data || []) 
      };
    } catch (error) {
      console.error('Error fetching event schedule:', error);
      return { 
        success: false, 
        data: [], 
        error: error.response?.data?.error || 'Failed to fetch event schedule' 
      };
    }
  },
  getEventStats: async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}/stats`);
      return { data: response.data, success: true };
    } catch (error) {
      console.error(`Error fetching stats for event ${eventId}:`, error);
      return { data: null, error: 'Failed to fetch event statistics', success: false };
    }
  }
};

// Feature Toggles API
export const featureToggleAPI = {
  getToggles: () => api.get('/feature-toggles'),
  getToggle: (name) => api.get(`/feature-toggles/${name}`),
  updateToggle: (name, isEnabled) => api.patch('/feature-toggles/update', { name, is_enabled: isEnabled })
};

// Payment API
export const paymentAPI = {
  // QR Code operations
  getQrCodes: () => api.get('/payment/qr-codes'),
  getQrCode: (id) => api.get(`/payment/qr-codes/${id}`),
  deleteQrCode: (id) => api.delete(`/payment/qr-codes/${id}`),
  activateQrCode: (id) => api.post(`/payment/qr-codes/${id}/activate`),
  // Transactions
  getTransactions: (params) => api.get('/payment/transactions', { params }),
  getTransaction: (id) => api.get(`/payment/transactions/${id}`),
  // Special handling for file uploads with authentication
  uploadQrCode: (formData) => {
    const token = localStorage.getItem('token');
    return api.post('/payment/qr-codes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });
  },
  // Feature toggle status
  getPaymentStatus: () => api.get('/payment/status')
};

// Subevent API
export const subeventAPI = {
  getSubevents: (params) => api.get('/comprehensive-crud/subevents', { params }),
  getSubevent: (id) => api.get(`/comprehensive-crud/subevents/${id}`),
  getSubeventsByEvent: (eventId) => api.get(`/comprehensive-crud/subevents?event_id=${eventId}`),
  createSubevent: (subeventData) => api.post('/comprehensive-crud/subevents', subeventData),
  updateSubevent: (id, subeventData) => api.put(`/comprehensive-crud/subevents/${id}`, subeventData),
  deleteSubevent: (id) => api.delete(`/comprehensive-crud/subevents/${id}`)
};

// RSVP API
export const rsvpAPI = {
  // RSVP operations
  getRsvps: (params) => api.get('/comprehensive-crud/guest-rsvp', { params }),
  getRsvp: (id) => api.get(`/comprehensive-crud/guest-rsvp/${id}`),
  createRsvp: (rsvpData) => api.post('/comprehensive-crud/guest-rsvp', rsvpData),
  updateRsvp: (id, rsvpData) => api.put(`/comprehensive-crud/guest-rsvp/${id}`, rsvpData),
  deleteRsvp: (id) => api.delete(`/comprehensive-crud/guest-rsvp/${id}`),
  
  // RSVP statistics
  getRsvpStats: (eventId) => api.get(`/rsvps/stats/${eventId}`),
  getRsvpStatsByEvent: () => api.get('/comprehensive-crud/guest-rsvp'),
  getGuestsByRsvpStatus: (eventId, status) => api.get(`/rsvp/guests/${eventId}?status=${status}`),
  
  // Bulk operations
  bulkUpdateRsvp: (rsvpData) => api.post('/rsvp/bulk-update', rsvpData),
  sendRsvpReminders: (guestIds) => api.post('/rsvp/send-reminders', { guest_ids: guestIds }),
  exportRsvpData: (eventId) => api.get(`/rsvp/export/${eventId}`, {
    responseType: 'blob'
  }),
  importRsvpData: (eventId, formData) => {
    const token = localStorage.getItem('token');
    return api.post(`/rsvp/import/${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

// Logistics API for room allocation and travel arrangements
export const logisticsAPI = {
  // Accommodation operations for room allocation
  getAccommodations: (params) => api.get('/comprehensive-crud/guest-accommodation', { params }),
  getAccommodation: (id) => api.get(`/comprehensive-crud/guest-accommodation/${id}`),
  createAccommodation: (data) => api.post('/comprehensive-crud/guest-accommodation', data),
  updateAccommodation: (id, data) => api.put(`/comprehensive-crud/guest-accommodation/${id}`, data),
  deleteAccommodation: (id) => api.delete(`/comprehensive-crud/guest-accommodation/${id}`),
  
  // Room allocation operations for guests
  getRoomAllocations: (params) => api.get('/comprehensive-crud/event-room-allocation', { params }),
  getRoomAllocationsByGuest: (guestId) => api.get(`/comprehensive-crud/event-room-allocation?guest_id=${guestId}`),
  getRoomAllocationsByEvent: (eventId) => api.get(`/comprehensive-crud/event-room-allocation?event_id=${eventId}`),
  allocateRoom: (allocationData) => api.post('/comprehensive-crud/event-room-allocation', allocationData),
  updateRoomAllocation: (id, allocationData) => api.put(`/comprehensive-crud/event-room-allocation/${id}`, allocationData),
  removeRoomAllocation: (id) => api.delete(`/comprehensive-crud/event-room-allocation/${id}`),
  // Bulk room allocation not supported by comprehensive-crud
  bulkAllocateRooms: (allocations) => Promise.reject('Bulk room allocation not supported'),
  
  // Travel arrangement operations
  getTravelArrangements: (params) => api.get('/comprehensive-crud/guest-travel', { params }),
  getTravelArrangementsByGuest: (guestId) => api.get(`/comprehensive-crud/guest-travel?guest_id=${guestId}`),
  getTravelArrangementsByEvent: (eventId) => api.get(`/comprehensive-crud/guest-travel?event_id=${eventId}`),
  createTravelArrangement: (data) => api.post('/comprehensive-crud/guest-travel', data),
  updateTravelArrangement: (id, data) => api.put(`/comprehensive-crud/guest-travel/${id}`, data),
  deleteTravelArrangement: (id) => api.delete(`/comprehensive-crud/guest-travel/${id}`),
  // Bulk travel arrangements not supported by comprehensive-crud
  bulkCreateTravelArrangements: (arrangements) => Promise.reject('Bulk travel arrangements not supported'),
  
  // Vehicle allocation operations
  // Vehicles endpoint not available - no master vehicles table
  getVehicles: (params) => Promise.resolve([]),
  // Vehicle endpoint not available
  getVehicle: (id) => Promise.reject('Vehicle endpoint not available'),
  // Vehicle creation not available
  createVehicle: (vehicleData) => Promise.reject('Vehicle creation not available'),
  // Vehicle update not available
  updateVehicle: (id, vehicleData) => Promise.reject('Vehicle update not available'),
  // Vehicle deletion not available
  deleteVehicle: (id) => Promise.reject('Vehicle deletion not available'),
  allocateVehicle: (allocationData) => api.post('/comprehensive-crud/guest-vehicle-allocation', allocationData),
  getVehicleAllocations: (params) => api.get('/comprehensive-crud/guest-vehicle-allocation', { params }),
  
  // Logistics reports
  // Logistics reports not available - would need custom implementation
  getLogisticsReports: (params) => Promise.resolve([]),
  // Logistics reports by type not available
  getLogisticsReportsByType: (type, params) => Promise.resolve([]),
  // Logistics report export not available
  exportLogisticsReport: (type, params) => Promise.reject('Report export not available'),

  // Logistics dashboard data
  // Logistics dashboard not available - use individual endpoints
  getDashboardData: (params) => Promise.resolve({}),
  // Dashboard summary not available
  getDashboardSummary: (eventId) => Promise.resolve({}),
  // Schedule overview not available
  getScheduleOverview: (date, eventId) => Promise.resolve([])
};

// Room Management API
export const roomAPI = {
  getRooms: (params) => api.get('/comprehensive-crud/rooms', { params }),
  getRoom: (id) => api.get(`/comprehensive-crud/rooms/${id}`),
  createRoom: (roomData) => api.post('/comprehensive-crud/rooms', roomData),
  updateRoom: (id, roomData) => api.put(`/comprehensive-crud/rooms/${id}`, roomData),
  deleteRoom: (id) => api.delete(`/comprehensive-crud/rooms/${id}`)
};

// Guest Management API
export const guestAPI = {
  getGuests: (params) => api.get('/guests', { params }),
  getGuest: (id) => api.get(`/guests/${id}`),
  createGuest: (guestData) => api.post('/guests', guestData),
  updateGuest: (id, guestData) => api.put(`/guests/${id}`, guestData),
  deleteGuest: (id) => api.delete(`/guests/${id}`),
  getGuestsByEvent: (eventId) => api.get(`/guests/event/${eventId}`),
  uploadBulkGuests: (formData) => api.post('/guests/bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  getDocuments: (guestId) => api.get(`/guests/${guestId}/documents`),
  uploadDocument: (guestId, formData) => api.post(`/guests/${guestId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
};

// Guest Groups API
export const guestGroupAPI = {
  getGuestGroups: (params) => api.get('/comprehensive-crud/guest-groups', { params }),
  getGuestGroup: (id) => api.get(`/comprehensive-crud/guest-groups/${id}`),
  createGuestGroup: (groupData) => api.post('/comprehensive-crud/guest-groups', groupData),
  updateGuestGroup: (id, groupData) => api.put(`/comprehensive-crud/guest-groups/${id}`, groupData),
  deleteGuestGroup: (id) => api.delete(`/comprehensive-crud/guest-groups/${id}`)
};

// Team and Employee Management API
export const teamAPI = {
  getTeams: (params) => api.get('/employee-management/teams', { params }),
  getTeam: (id) => api.get(`/employee-management/teams/${id}`),
  createTeam: (teamData) => api.post('/employee-management/teams', teamData),
  updateTeam: (id, teamData) => api.put(`/employee-management/teams/${id}`, teamData),
  deleteTeam: (id) => api.delete(`/employee-management/teams/${id}`),
  getTeamMembers: (teamId) => api.get(`/employee-management/teams/${teamId}/members`),
  addTeamMember: (teamId, employeeId) => api.post(`/employee-management/teams/${teamId}/members`, { employee_id: employeeId }),
  removeTeamMember: (teamId, employeeId) => api.delete(`/employee-management/teams/${teamId}/members/${employeeId}`)
};

export const employeeAPI = {
  getEmployees: (params) => api.get('/employee-management/employees', { params }),
  getEmployee: (id) => api.get(`/employee-management/employees/${id}`),
  createEmployee: (employeeData) => api.post('/employee-management/employees', employeeData),
  updateEmployee: (id, employeeData) => api.put(`/employee-management/employees/${id}`, employeeData),
  deleteEmployee: (id) => api.delete(`/employee-management/employees/${id}`),
  getDepartments: () => api.get('/employee-management/departments'),
  createDepartment: (departmentData) => api.post('/employee-management/departments', departmentData),
  updateDepartment: (id, departmentData) => api.put(`/employee-management/departments/${id}`, departmentData),
  deleteDepartment: (id) => api.delete(`/employee-management/departments/${id}`)
};

// Vendor Management API
export const vendorAPI = {
  getVendors: (params) => api.get('/vendors', { params }),
  getVendor: (id) => api.get(`/vendors/${id}`),
  createVendor: (vendorData) => api.post('/vendors', vendorData),
  updateVendor: (id, vendorData) => api.put(`/vendors/${id}`, vendorData),
  deleteVendor: (id) => api.delete(`/vendors/${id}`),
  getVendorTypes: () => api.get('/vendors/types')
};

// Venue Management API
export const venueAPI = {
  // Basic CRUD operations
  getAllVenues: (params) => api.get('/venues', { params }),
  getCustomerVenues: (customerId, params) => api.get(`/venues/customer/${customerId}`, { params }),
  getVenue: (id) => api.get(`/venues/${id}`),
  createVenue: (venueData) => api.post('/venues', venueData),
  updateVenue: (id, venueData) => api.put(`/venues/${id}`, venueData),
  deleteVenue: (id) => api.delete(`/venues/${id}`),
  
  // Additional operations
  getVenueEvents: (venueId) => api.get(`/venues/${venueId}/events`),
  checkVenueAvailability: (venueId, startDate, endDate) => api.get(`/venues/${venueId}/availability`, { params: { start_date: startDate, end_date: endDate } }),
  getCustomers: () => api.get('/customers'),
  getChecklist: (venueId) => api.get(`/venues/${venueId}/checklist`),
  updateChecklist: (venueId, checklist) => api.post(`/venues/${venueId}/checklist`, { checklist })
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Invite API
export const inviteAPI = {
  // Get invites by event
  getInvitesByEvent: (eventId) => api.get(`/invites/by-event/${eventId}`),

  // Get invite details
  getInvite: (id) => api.get(`/invites/${id}`),
  getInviteVersions: (inviteId) => api.get(`/invites/${inviteId}/versions`),

  // Create invites
  createInvite: (inviteData) => api.post('/invites', inviteData),
  createInviteVersion: (inviteId, versionData) => api.post(`/invites/${inviteId}/versions`, versionData),

  // Update invites
  updateInvite: (id, inviteData) => api.put(`/invites/${id}`, inviteData),

  // Delete invites
  deleteInvite: (id) => api.delete(`/invites/${id}`),

  // Media upload
  uploadMedia: (formData) => {
    const token = localStorage.getItem('token');
    return api.post('/invites/upload-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Send invites
  sendPreview: (data) => api.post('/invites/send-preview', data),
  sendInvites: (versionId, guestIds) => api.post(`/invites/${versionId}/send`, { guest_ids: guestIds }),

  // Analytics
  getAnalytics: (versionId) => api.get(`/invites/${versionId}/analytics`),

  // WhatsApp webhook
  webhookVerify: (params) => api.get('/invites/webhook', { params }),
  webhookProcess: (data) => api.post('/invites/webhook', data),

  // RSVP confirmation
  verifyRsvpToken: (token) => api.get(`/invites/rsvp/verify/${token}`),
  submitRsvpResponse: (token, data) => api.post(`/invites/rsvp/respond/${token}`, data),

  // WhatsApp messaging
  sendWhatsAppMessage: (guestId, message) => api.post('/invites/send-whatsapp', { guest_id: guestId, message }),
};

export default api;
